/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Stepper';

const StepperManager: ComponentManagerFn<'Stepper'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Stepper'>(platform, 'Stepper'),
    type: 'Stepper',
    name: 'Stepper',
    isChildAllowed: (page, item, childType) =>
      (['Step'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['Step', 'Step'],
    props: {
      color: {
        label: 'Connector Color',
        type: 'ThemeBGColor',
        required: true,
        helpText:
          'The color of the component. It supports those theme colors that make sense for this component.',
      },
      size: {
        label: 'Size',
        type: 'number',
        helpText: 'Specify the size of each step.',
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: { vertical: 'Vertical', horizontal: 'Horizontal' },
        required: true,
        helpText: 'The orientation of the steps.',
      },
      alternativeLabel: {
        label: 'Alternative Label',
        type: 'boolean',
        helpText: 'Alternative Label of the step',
      },
      activeStepId: {
        label: 'Active Step Id',
        type: 'string',
        helpText: 'Specify the item id of an step to display as active',
      },
    },
    category: 'Data',
    canHaveChildren: () => true,
    getDisplayText: (item) => '',
    icon: { iconName: 'server' },
    description: `**Overview**<br/>
The Stepper component is a navigation controller designed to guide users through a linear process, such as a checkout flow, a survey, or a multi-page form.
Unlike a Tab Panel which allows random access to content, a Stepper enforces order. It visually links a series of Step components, showing the user exactly where they are in the sequence ("Step 2 of 4") and what has already been completed.

**1. Structure & Layout**<br/>
The Stepper defines the "road" that the user travels.
* **Orientation:** Controls the direction of the flow.
    * **Horizontal:** Steps are arranged left-to-right. Best for desktop wizards with 3-5 steps.
    * **Vertical:** Steps are stacked top-to-bottom. Best for mobile layouts or complex processes with many steps.
* **Active Step Id:** The identifier of the step currently being viewed. (Usage: By binding this property to a variable, you can programmatically jump the user to a specific step, e.g., clicking "Next" updates the variable to the ID of Step 2).

**2. Visual Customization**<br/>
You can tailor the look of the progress track to match your brand or the specific context of the task.
* **Connector Color:** Sets the color of the line that connects the steps (e.g., Primary, Success). This visually reinforces the path the user is taking.
* **Size:** Controls the scale of the step indicators (circles/icons).
* **Alternative Label:** Changes the label placement. By default, labels might be inline; enabling this typically moves labels below the icons (in horizontal mode) for better readability.

**3. Content Composition**<br/>
The Stepper is a strict container. It is designed to hold only one type of child component.
* **Allowed Children:** You can only add Step components directly to a Stepper.
* **Why?** The Stepper's job is to manage the sequence. The actual content (forms, text, buttons) lives inside the individual Step components.
* **Default State:** When you first add a Stepper to the page, it automatically creates two empty Step children to get you started.`,
  };
};

export default StepperManager;
